import { css } from '@emotion/react'
import {
  Button,
  Flow,
  Heading,
  Icon,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalProps,
  Switch,
  Tag,
  Text,
  Theme,
  Tooltip,
  useTheme,
} from 'bold-ui'
import { Box } from 'components/Box'
import { ResponsiveModal } from 'components/responsive'
import { useCallback, useMemo, useState } from 'react'
import { mediaMobile } from 'util/breakpoints'

interface CookiePreferencesModalProps extends Omit<ModalProps, 'open'> {
  onClickAceitarNecessarios: () => void
  onClickAceitarTodos: () => void
  onOpenTermosUso: () => void
}

export const CookiePreferencesModal = (props: CookiePreferencesModalProps) => {
  const { onClickAceitarNecessarios, onClickAceitarTodos, onOpenTermosUso, ...rest } = props
  const theme = useTheme()
  const [enableCookiesDesempenho, setEnableCookiesDesempenho] = useState(false)
  const styles = useMemo(() => createStyles(theme, enableCookiesDesempenho), [theme, enableCookiesDesempenho])

  const toggleEnableCookiesDesempenho = useCallback(() => {
    setEnableCookiesDesempenho((prev) => !prev)
  }, [])

  return (
    <ResponsiveModal open {...rest}>
      <ModalHeader title='Configurações avançadas de cookies' icon={{ name: 'cookieOutline', fill: 'primary' }} />
      <ModalBody style={styles.modalBody}>
        <Flow direction='vertical' gap={2}>
          <Box>
            <div css={styles.declaracao}>
              <Text>
                Para melhorar a sua experiência no sistema e prover serviços personalizados, utilizamos cookies.
              </Text>
              <Button
                kind='normal'
                skin='default'
                size='small'
                style={styles.declaracaoButton}
                onClick={onOpenTermosUso}
              >
                <Icon icon='fileVisualizationOutline' />
                Ver declaração de cookies
              </Button>
            </div>
          </Box>
          <Flow direction='vertical' gap={1}>
            <Box>
              <div css={styles.cookieGrid}>
                <Heading level={3} style={styles.cookieHeading}>
                  Cookies estritamente necessários
                </Heading>
                <Text style={styles.cookieText}>
                  Esses cookies permitem funcionalidades essenciais, tais como a manutenção e segurança da sessão do
                  usuário. Esses cookies não podem ser desativados em nossos sistemas. Embora sejam necessários, você
                  pode bloquear esses cookies diretamente no seu navegador, mas isso pode comprometer sua experiência e
                  prejudicar o funcionamento do site.
                </Text>
                <div css={[styles.cookieSwitch, styles.cookiesEssenciaisSwitchState]}>
                  Ativado
                  <Tooltip text='Cookies obrigatórios não podem ser desativados'>
                    <Tag style={styles.switchLocked}>
                      <Icon icon='lockCloseFilled' style={styles.lock} size={1} />
                    </Tag>
                  </Tooltip>
                </div>
              </div>
            </Box>
            <Box>
              <div css={styles.cookieGrid}>
                <Heading level={3} style={styles.cookieHeading}>
                  Cookies de desempenho
                </Heading>
                <Text style={styles.cookieText}>
                  Cookies de desempenho visam a melhoria do desempenho do site por meio da coleta de dados anonimizados
                  sobre navegação e uso dos recursos disponibilizados. Se você não permitir a coleta desses cookies,
                  esses dados não serão usados para melhoria do site.
                </Text>
                <div css={[styles.cookieSwitch, styles.cookiesDesempenhoSwitchState]}>
                  {enableCookiesDesempenho ? 'Ativado' : 'Desativado'}
                  <Switch onChange={toggleEnableCookiesDesempenho} />
                </div>
              </div>
            </Box>
          </Flow>
        </Flow>
      </ModalBody>
      <ModalFooter>
        <div css={styles.footer}>
          <Button onClick={onClickAceitarTodos} style={styles.aceitarButton}>
            Aceitar todos
          </Button>
          <Button
            onClick={enableCookiesDesempenho ? onClickAceitarTodos : onClickAceitarNecessarios}
            kind='primary'
            style={styles.salvarButton}
          >
            Salvar configuração
          </Button>
        </div>
      </ModalFooter>
    </ResponsiveModal>
  )
}

const createStyles = (theme: Theme, cookiesDesempenhoState: boolean) => {
  return {
    modalBody: css`
      ${mediaMobile} {
        padding: 1rem;
      }
    `,
    declaracao: css`
      display: flex;
      align-items: center;
      gap: 0.5rem;
      ${mediaMobile} {
        flex-direction: column;
        align-items: stretch;
      }
    `,
    declaracaoButton: css`
      flex-shrink: 0;
      width: 14rem;
      height: 2rem;
      color: ${theme.pallete.primary.c40};
      border-color: ${theme.pallete.primary.c40};
      ${mediaMobile} {
        align-self: flex-end;
      }
    `,
    cookieGrid: css`
      display: grid;
      align-items: center;
      gap: 0.5rem;
      grid-template-columns: 1fr auto;
      grid-template-areas:
        'heading switch'
        'text text';
      ${mediaMobile} {
        grid-template-columns: 1fr;
        grid-template-areas:
          'heading'
          'text'
          'switch';
      }
    `,
    cookieHeading: css`
      grid-area: heading;
    `,
    cookieText: css`
      grid-area: text;
    `,
    cookieSwitch: css`
      grid-area: switch;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 0.5rem;
      font-weight: bold;
      font-size: 1rem;
    `,
    aceitarButton: css`
      width: 10.5rem;
      ${mediaMobile} {
        width: 100%;
      }
    `,
    salvarButton: css`
      width: 13rem;
      ${mediaMobile} {
        width: 100%;
      }
    `,
    footer: css`
      display: flex;
      justify-content: space-between;
      gap: 1rem;
      ${mediaMobile} {
        flex-direction: column;
      }
    `,
    cookiesEssenciaisSwitchState: css`
      color: ${theme.pallete.primary.c40};
    `,
    cookiesDesempenhoSwitchState: css`
      color: ${cookiesDesempenhoState ? theme.pallete.primary.c40 : theme.pallete.gray.c40};
    `,
    switchLocked: css`
      display: flex;
      align-items: center;
      justify-content: center;
      width: 3rem;
      height: 1.5rem;
      background-color: ${theme.pallete.primary.c80};
      border-radius: 0.75rem;
    `,
    lock: css`
      fill: ${theme.pallete.gray.c100};
    `,
  }
}
